import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { toTitleCase } from "typed-case";

// The named sizes in global.css (`text-readout`, `text-display`) are font
// sizes; without this, tailwind-merge reads them as colours and drops a
// `text-<colour>` next to them.
const twMerge = extendTailwindMerge({
  extend: { classGroups: { "font-size": [{ text: ["readout", "display"] }] } },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function filterNulls<T>(array: (T | null | undefined)[]): T[] {
  return array.filter((item): item is T => item !== null && item !== undefined);
}

export function stringOrNull(value: number | string | null): string | null {
  if (value === "") return null;
  return value === null ? null : value.toString();
}

export function numberOrNull(value: number | string | null): number | null {
  const num = Number(value);
  return isNaN(num) ? null : num;
}

export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w-]+/g, "") // Remove all non-word chars
    .replace(/--+/g, "-"); // Replace multiple - with single -
}

export function toCodeIdentifier(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/^[^a-z_]+/, "");
}

export function isNumber(value: unknown): boolean {
  return typeof value === "number" || isFinite(Number(value));
}

export function screamingSnakeCaseToTitle(value: string): string {
  return value
    .split("_")
    .map((s) => toTitleCase(s))
    .join(" ");
}

export function truncateText(text: string, max_length = 25): string {
  if (text.length <= max_length) return text;
  return text.slice(0, max_length) + "...";
}

// Middle-truncate a filename, preserving the extension: "report.pdf" stays
// readable as "really-long-na….pdf" rather than losing the ".pdf".
export function truncateFilename(name: string, max_length = 28): string {
  if (name.length <= max_length) return name;
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return name.slice(0, max_length - 1) + "…";
  const ext = name.slice(dot);
  const keep = max_length - ext.length - 1;
  if (keep <= 0) return "…" + ext;
  return name.slice(0, keep) + "…" + ext;
}

/**
 * Display-format a number, shortening over-precise values to `maxSigFigs`
 * significant figures with a trailing ellipsis. Returns the full string too, so
 * callers can show it on hover. Values already within the limit pass through
 * unchanged (no rounding, no ellipsis), so user-entered numbers read verbatim.
 */
export function truncateNumber(
  value: number,
  maxSigFigs = 6,
): { text: string; full: string; truncated: boolean } {
  const full = String(value);
  if (!Number.isFinite(value)) return { text: full, full, truncated: false };
  const rounded = Number(value.toPrecision(maxSigFigs));
  if (rounded === value) return { text: full, full, truncated: false };
  return { text: `${rounded}…`, full, truncated: true };
}

